import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _objectDestructuringEmpty from "@babel/runtime/helpers/esm/objectDestructuringEmpty";
const _excluded = ["scopes"];
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { CONTENT_ACTION_IDS } from 'content-management-actions-lib/constants/actionIds';
import AsyncContentAccessControlPanel from 'content-management-actions-lib/modals/AsyncContentAccessControlPanel';
import { LimitTypes, useContentUsageLimits } from 'content-management-restrictions-lib';
import { ActionIdsWithoutSuccessBanners, AssignUsersAndTeamsConfig, PageRunTestConfig, SitePageEditConfig, MoveToLandingPagesConfig, SitePageAddToLangGroupConfig, SitePageArchiveConfig, SitePageDeleteConfig, SitePageDetailsConfig, SitePagePublishingOptionsConfig, SitePageRemoveFromLangGroupConfig, SitePageUnpublishingOptionsConfig, ViewConfig } from 'page-lib/constants/actions';
import { MARKETING_CONTENT_RESTRICTED_WRITE, SITE_PAGES_PASSWORD_PROTECTION, SITE_PAGES_PRIVATE_CONTENT_MANAGE, SITE_PAGES_PUBLISH, SITE_PAGES_WRITE } from 'page-lib/constants/scopes';
import { displayAndTrackAudienceAccessUpdateSuccess, displayAudienceAccessUpdateError } from 'page-lib/utils/audienceAccess';
import { useUpgradeModal } from 'ui-addon-upgrades/ums/useUpgradeModal';
import { AccessControlTypes } from 'ui-memberships-settings-lib/constants/AccessControlTypes';
import { ContentTypes } from 'ui-memberships-settings-lib/constants/ContentTypes';
import { getContentAccessControlEditPermissionsDisabledTooltipI18nKey } from 'ui-memberships-settings-lib/utils/tooltips';
import SocialComposerPanelWrapper from '../modals/SocialComposerPanelWrapper';
import { PromoteAdCampaign } from '../constants/actions';
import { getHasSocialAccess } from '../selectors/auth';
import { displayActionErrorMessage, displayActionSuccessMessage, displayCreateLanguageVariationErrorMessage } from '../utils/actions';
import { usageTracker } from '../utils/usageTracker';
import AsyncClonePageModal from 'page-lib/components/modals/AsyncClonePageModal';
import { SLPC_AI_CLONE_FLOW } from 'page-lib/constants/gates';
import CMPCloneModal from 'content-management-actions-lib/modals/CloneModal';
import { jsx as _jsx } from "react/jsx-runtime";
const DeleteLangGroupConfig = {
  id: CONTENT_ACTION_IDS.DELETE_LANGUAGE_GROUP,
  displayPermissions: {
    custom: () => false
  }
};
const getUserHasManagePrivateContentAccess = scopes => scopes.includes(SITE_PAGES_PRIVATE_CONTENT_MANAGE);
const useSitePageActionsConfigOptions = () => {
  const {
    getQuantityUsedValue
  } = useContentUsageLimits();
  const cloneUpgradeModalName = getQuantityUsedValue(LimitTypes.CONTENT_OBJECT_LIMIT) < 40 ? 'site-pages-starter-clone-button-details-page' : 'site-pages-clone-button';
  const {
    openUpgradeModal
  } = useUpgradeModal(cloneUpgradeModalName);
  const {
    openUpgradeModal: openPromoteSocialPostUpgradeModal
  } = useUpgradeModal('social-share-on-details-page');
  const {
    openUpgradeModal: openPasswordProtectionUpgradeModal
  } = useUpgradeModal('memberships-content-password-protection');
  const hasSocialAccess = useSelector(getHasSocialAccess);
  const CloneActionConfig = useMemo(() => {
    return {
      id: CONTENT_ACTION_IDS.CLONE,
      onActionButtonClick: () => {
        openUpgradeModal();
      },
      lockPermissions: {
        custom: ({
          permissionsExtraData
        }) => permissionsExtraData === null || permissionsExtraData === void 0 ? void 0 : permissionsExtraData.limitData.hasReachedContentObjectLimit
      },
      displayPermissions: {
        scopes: [SITE_PAGES_WRITE]
      },
      ModalOrPanel: _ref => {
        let props = Object.assign({}, (_objectDestructuringEmpty(_ref), _ref));
        const gates = props.gates;
        if (gates.includes(SLPC_AI_CLONE_FLOW)) {
          return /*#__PURE__*/_jsx(AsyncClonePageModal, Object.assign({}, props));
        }
        return /*#__PURE__*/_jsx(CMPCloneModal, Object.assign({}, props));
      }
    };
  }, [openUpgradeModal]);
  const PromoteSocialPost = useMemo(() => {
    return {
      id: CONTENT_ACTION_IDS.PROMOTE_SOCIAL_POST,
      name: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "page-details-ui.actions.names.promoteSocialPost"
      }),
      onActionButtonClick: () => {
        openPromoteSocialPostUpgradeModal();
      },
      ModalOrPanel: SocialComposerPanelWrapper,
      tableRowActionGroup: 1,
      orderInTableRowActionGroup: 1,
      lockPermissions: {
        custom: () => !hasSocialAccess
      }
    };
  }, [openPromoteSocialPostUpgradeModal, hasSocialAccess]);
  const AudienceAccessConfig = useMemo(() => {
    return {
      id: CONTENT_ACTION_IDS.CONTROL_AUDIENCE_ACCESS,
      onActionButtonClick: () => {
        openPasswordProtectionUpgradeModal();
      },
      enablePermissions: {
        custom: ({
          scopes
        }) => scopes.includes(MARKETING_CONTENT_RESTRICTED_WRITE) && getUserHasManagePrivateContentAccess(scopes)
      },
      lockPermissions: {
        scopes: [MARKETING_CONTENT_RESTRICTED_WRITE]
      },
      DisabledTooltipContent: () => /*#__PURE__*/_jsx(FormattedMessage, {
        message: getContentAccessControlEditPermissionsDisabledTooltipI18nKey(ContentTypes.SITE_PAGE)
      }),
      ModalOrPanel: _ref2 => {
        let {
            scopes
          } = _ref2,
          props = _objectWithoutPropertiesLoose(_ref2, _excluded);
        const userHasManagePrivateContentAccess = getUserHasManagePrivateContentAccess(scopes);
        return /*#__PURE__*/_jsx(AsyncContentAccessControlPanel, Object.assign({}, props, {
          userCanViewContentAccessControl: scopes.includes(MARKETING_CONTENT_RESTRICTED_WRITE),
          userHasManagePrivateContentAccess: userHasManagePrivateContentAccess,
          userHasContentTypePublishAccess: scopes.includes(SITE_PAGES_PUBLISH),
          accessControlContentType: ContentTypes.SITE_PAGE,
          accessControlOptions: {
            [AccessControlTypes.PUBLIC]: true,
            [AccessControlTypes.PASSWORD]: true,
            [AccessControlTypes.ACCESS_GROUP_MEMBERSHIP]: true,
            [AccessControlTypes.SSO]: true,
            [AccessControlTypes.SSO_WITH_ACCESS_GROUPS]: true
          },
          hasPasswordProtectionAccess: scopes.includes(SITE_PAGES_PASSWORD_PROTECTION),
          baseRequestPath: "/page-management/v1/site-page/bulk/update-audience-access"
        }));
      }
    };
  }, [openPasswordProtectionUpgradeModal]);
  return {
    onActionError: ({
      actionId,
      error,
      bulkActionErrorDetailsModalButton
    }) => {
      if (actionId === CONTENT_ACTION_IDS.CONTROL_AUDIENCE_ACCESS) {
        displayAudienceAccessUpdateError({
          isLandingPage: false
        });
      } else if (actionId === CONTENT_ACTION_IDS.CREATE_LANGUAGE_VARIANT) {
        displayCreateLanguageVariationErrorMessage({
          isLandingPage: false,
          error
        });
      } else {
        displayActionErrorMessage({
          isLandingPage: false,
          actionId,
          bulkActionErrorDetailsModalButton
        });
      }
    },
    onActionSuccess: ({
      actionId,
      data
    }) => {
      if (!ActionIdsWithoutSuccessBanners.includes(actionId)) {
        if (actionId === CONTENT_ACTION_IDS.CONTROL_AUDIENCE_ACCESS) {
          var _data$actionData, _data$actionData2;
          displayAndTrackAudienceAccessUpdateSuccess({
            isLandingPage: false,
            publicAccessRuleType: data === null || data === void 0 || (_data$actionData = data.actionData) === null || _data$actionData === void 0 ? void 0 : _data$actionData.accessRuleType,
            password: data === null || data === void 0 || (_data$actionData2 = data.actionData) === null || _data$actionData2 === void 0 ? void 0 : _data$actionData2.password
          });
        } else {
          displayActionSuccessMessage({
            isLandingPage: false,
            actionId
          });
        }
        usageTracker.track('actionsInteraction', {
          action: actionId
        });
      }
    },
    actionsConfigOverrides: [AssignUsersAndTeamsConfig, AudienceAccessConfig, CloneActionConfig, DeleteLangGroupConfig, PageRunTestConfig, SitePageEditConfig, MoveToLandingPagesConfig, PromoteAdCampaign, PromoteSocialPost, SitePageArchiveConfig, SitePageDeleteConfig, SitePageDetailsConfig, SitePagePublishingOptionsConfig, SitePageUnpublishingOptionsConfig, SitePageAddToLangGroupConfig, SitePageRemoveFromLangGroupConfig, ViewConfig]
  };
};
export default useSitePageActionsConfigOptions;